/**
 * ENG-8924: when Plans & Billing shows the Request License page instead of the plan
 * chooser, and the copy helpers it shares with the add-on gate modals (ENG-8922).
 *
 * Intentionally uses structural types (no @coeff/api imports) so this module can be unit-tested
 * without ts-jest encountering @coeff/api's compiled JS -- same as enterpriseBlocker.utils.ts.
 */
import { isPaidPlanType } from "./enterpriseBlocker.utils";

type PlanManagerLike = { email: string; display_name?: string };

export type PlanToJoinLike = {
  plan_type: string;
  plan_display_name: string;
  plan_managers: PlanManagerLike[];
  can_purchase_own_plan?: boolean;
};

type ClientFlagsLike = {
  plan_type?: string | null;
  is_plan_manager?: boolean;
  admin_emails?: string[];
  domain_has_enterprise_subscription?: boolean;
  enforce_billing_checkout_enterprise_blocker?: boolean;
  premium_gate_plan_to_join?: PlanToJoinLike;
  general_gate_plan_to_join?: PlanToJoinLike;
  force_hide_billing_ui?: boolean;
};

export type RequestLicenseGate = {
  // Sent with the request so the server re-checks the same rule (ENG-8920).
  gateType: "premium_connector" | "general";
  planToJoin: PlanToJoinLike;
};

const canManageBilling = (clientFlags: ClientFlagsLike, currentUserEmail?: string): boolean =>
  Boolean(clientFlags.is_plan_manager) ||
  Boolean(currentUserEmail && (clientFlags.admin_emails ?? []).includes(currentUserEmail));

const isPremiumCheckoutBlocked = (
  clientFlags: ClientFlagsLike,
  premiumConnectorsUsedCount: number
): boolean =>
  Boolean(clientFlags.enforce_billing_checkout_enterprise_blocker) &&
  premiumConnectorsUsedCount > 0;

/**
 * The Request License page applies to a seatless user who isn't on a paid plan of their own and
 * doesn't manage billing (plan managers and admins manage the plan; they never request to join it).
 * - When the premium-connector checkout blocker applies, the premium gate decides: a plan to join
 *   -> Request License; none -> undefined, and the caller keeps its Contact Admin / Upgrade Plan
 *   (Contact Sales) screens.
 * - Otherwise the general gate decides. No plan to join -> undefined -> the plan chooser, unchanged.
 */
export const getRequestLicenseGate = (
  clientFlags: ClientFlagsLike | undefined,
  currentUserEmail: string | undefined,
  premiumConnectorsUsedCount: number
): RequestLicenseGate | undefined => {
  if (
    !clientFlags ||
    isPaidPlanType(clientFlags.plan_type) ||
    canManageBilling(clientFlags, currentUserEmail)
  ) {
    return undefined;
  }
  const [gateType, planToJoin] = isPremiumCheckoutBlocked(clientFlags, premiumConnectorsUsedCount)
    ? (["premium_connector", clientFlags.premium_gate_plan_to_join] as const)
    : (["general", clientFlags.general_gate_plan_to_join] as const);
  if (!planToJoin || planToJoin.plan_managers.length === 0) {
    return undefined;
  }
  return { gateType, planToJoin };
};

// ENG-9319: the `?from=` the add-on gates' "Or, purchase your own plan" link opens Plans & Billing
// with. Goes straight to the plan chooser where buying your own plan is allowed.
export const PURCHASE_OWN_PLAN_FROM = "purchase_own_plan";

/**
 * ENG-9319: whether the Request License page also offers "Or, purchase your own plan". Only when
 * every team plan in the domain family is Pro (the server's can_purchase_own_plan; an Enterprise or
 * Custom plan anywhere in the family keeps Request License the only path), and only on the general
 * gate: a Pro plan of their own wouldn't unlock premium connectors. The server applies the same
 * rule at checkout.
 */
export const canPurchaseOwnPlan = (gate: RequestLicenseGate): boolean =>
  gate.gateType === "general" && Boolean(gate.planToJoin.can_purchase_own_plan);

/**
 * Whether Plans & Billing shows the Request License page: there's a plan to join, unless the user
 * chose to buy their own plan (on the page, or via the add-on link) where that's allowed.
 */
export const shouldShowRequestLicensePage = (
  gate: RequestLicenseGate | undefined,
  choseOwnPlan: boolean
): gate is RequestLicenseGate => gate !== undefined && !(choseOwnPlan && canPurchaseOwnPlan(gate));

/**
 * force_hide_billing_ui (Cartier/Richemont's show_plans_and_billing_only_for_admins_and_managers
 * gate, kept on purpose -- Kurt 10/8) keeps everyone but Plan Managers and admins out of Plans &
 * Billing. The page still opens for one thing: a user with a plan to join gets the Request License
 * page (no prices, no checkout), so the countdown email's Request License link (ENG-8861) doesn't
 * dead-end on the dashboard. Which gate's plan applies is decided once metadata loads; this only
 * says whether either could.
 */
export const canRequestLicenseWhileBillingHidden = (
  clientFlags: ClientFlagsLike | undefined,
  currentUserEmail: string | undefined
): boolean =>
  Boolean(clientFlags?.force_hide_billing_ui) &&
  [0, 1].some(premiumConnectorsUsedCount =>
    Boolean(getRequestLicenseGate(clientFlags, currentUserEmail, premiumConnectorsUsedCount))
  );

// What joining the plan gives (Kurt's 9/30 review: one list for Pro, one for Enterprise; copy final
// as of the 10/1 design review). The list follows the gate, not the plan being joined (10/2 review,
// matching the mock): the general gate always shows the Pro list -- an Enterprise or Custom plan
// includes all of it -- and only the premium-connector gate shows the Enterprise list. Premium
// connectors are Enterprise only, so the Pro list never mentions them.
const PRO_PLAN_FEATURES = [
  "Scheduled auto-refresh",
  "No row limits",
  "Unlimited run usage",
  "Shared connections",
];

export const getRequestLicenseFeatures = (
  gateType: RequestLicenseGate["gateType"],
  premiumConnectorNames?: string
): string[] =>
  gateType === "premium_connector"
    ? [
        premiumConnectorNames
          ? `Connect to ${premiumConnectorNames} and all premium data sources`
          : "Connect to all premium data sources",
        "Admin controls & user permissions",
        "For teams with 5+ users",
        "Unlimited run usage + much more",
      ]
    : PRO_PLAN_FEATURES;

// Kurt's 9/30 review: two names read better than three.
const MAX_NAMED_PLAN_MANAGERS = 2;

const planManagerShortName = (manager: PlanManagerLike): string =>
  manager.display_name?.trim().split(/\s+/)[0] || manager.email;

// "Tom" / "Tom, Dan & 2 more" -- first names, falling back to the email.
export const formatPlanManagerNames = (managers: PlanManagerLike[]): string => {
  const names = managers.slice(0, MAX_NAMED_PLAN_MANAGERS).map(planManagerShortName);
  const remaining = managers.length - names.length;
  return remaining > 0 ? `${names.join(", ")} & ${remaining} more` : names.join(", ");
};

// 10/1 design review: after a request, the button is greyed out for 7 days and says when it was
// sent; then it's active again so they can re-request (which re-emails the plan managers). Counted
// in calendar days, so "today" / "yesterday" match the user's clock. Same rule as the add-on gates.
export const LICENSE_REQUEST_COOLDOWN_DAYS = 7;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const daysSinceLicenseRequest = (requestedAt: Date, now: Date = new Date()): number => {
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.max(0, Math.round((startOfDay(now) - startOfDay(requestedAt)) / MS_PER_DAY));
};

// The request that still greys out the button, or undefined once it's 7+ days old (or none).
export const getActiveLicenseRequestDate = (
  pendingLicenseRequestDt: string | Date | null | undefined,
  now: Date = new Date()
): Date | undefined => {
  if (!pendingLicenseRequestDt) {
    return undefined;
  }
  const requestedAt = new Date(pendingLicenseRequestDt);
  return daysSinceLicenseRequest(requestedAt, now) < LICENSE_REQUEST_COOLDOWN_DAYS
    ? requestedAt
    : undefined;
};

// The plan manager names can wrap inside the parentheses (10/6 review), yet their tooltip still
// centers under the names (10/2 review): each word is its own one-line trigger, so it centers under
// the word you hover. A word keeps its trailing space, which keeps the dotted underline continuous
// and is where a line may break. Same as the add-on.
export const splitIntoHoverWords = (text: string): string[] =>
  text.split(" ").map((word, i, words) => (i < words.length - 1 ? `${word} ` : word));

// "Requested today" / "Requested yesterday" / "Requested 3 days ago".
export const formatRequestedAgo = (requestedAt: Date, now: Date = new Date()): string => {
  const days = daysSinceLicenseRequest(requestedAt, now);
  if (days === 0) {
    return "Requested today";
  }
  if (days === 1) {
    return "Requested yesterday";
  }
  return `Requested ${days} days ago`;
};
